import { LDProvider, useLDClient } from 'launchdarkly-react-client-sdk'
import React, { useEffect, useMemo, useState } from 'react'

import BusyOverlay from 'components/common/BusyOverlay'
import useAuth from 'hooks/use-auth'

/**
 * This list will provide sane default values for feature flags in the event that LD has an outage
 * and the LD SDK can't retrieve the feature flags from LD's servers
 */
const DEFAULT_FEATURE_FLAG_VALUES = {
  'user-level-api-keys': false,
  'pricing-usage': false,
  'expose-client-status': false,
  'create-gamedays': false,
  'ad-hoc-service-attack': false,
  'jira-integration': false,
  'team-name-change': false,
  'private-network-integration-always': false,
  'private-network-integration': false,
  'user-defined-dependencies': false,
  'dependency-suggestions': false,
  'cancel-subscription': false
}

function useUserContext() {
  const { currentTeamId, currentTeam, companyId, tier, session } = useAuth()

  const userContext = useMemo(
    () => ({
      key: session.identifier,
      email: session.identifier,
      custom: {
        companyId: companyId,
        companyName: session.company_name,
        tier: tier,
        teamName: currentTeam.name,
        teamId: currentTeamId,
        role: currentTeam.role
      }
    }),
    [
      companyId,
      currentTeam.name,
      currentTeam.role,
      currentTeamId,
      session.company_name,
      session.identifier,
      tier
    ]
  )
  return userContext
}

const LaunchDarklyClientLoader: React.FC<{ children: JSX.Element }> = ({
  children
}) => {
  const ldClient = useLDClient()
  const userContext = useUserContext()
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    async function identify(user) {
      try {
        await ldClient.identify(user)
      } catch (e) {
        // swallow ld error
      }
    }

    if (isReady) {
      identify(userContext)
    }
  }, [
    isReady,
    userContext,
    ldClient,
    userContext.custom.teamId,
    userContext.email
  ])

  useEffect(() => {
    ldClient &&
      ldClient
        .waitUntilReady()
        .then(() => {
          setIsReady(true)
          // Set on window like the default client would so we can use this in other areas
          window.ldClient = ldClient
        })
        .catch(() => {}) // swallow ld error
  }, [ldClient])

  return isReady ? children : <BusyOverlay />
}

export const LaunchDarklyProvider: React.FC<{ children: JSX.Element }> = ({
  children
}) => {
  const userContext = useUserContext()

  return (
    <LDProvider
      clientSideID={process.env.REACT_APP_LAUNCHDARKLY_CLIENT_SIDE_ID}
      deferInitialization={false}
      options={
        window.Cypress
          ? { streaming: false, sendEvents: false }
          : {
              // set default values in case there's an outage with LD so we can still render
              bootstrap: DEFAULT_FEATURE_FLAG_VALUES
            }
      }
      user={userContext}
    >
      <LaunchDarklyClientLoader>{children}</LaunchDarklyClientLoader>
    </LDProvider>
  )
}
